import React from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { allocateInstallationTime, fetchInstallationTimeOptions, type TimeEntry } from '@/data/scheduleTimeTracking';
import { useModalScrollLock } from '@/hooks/useModalScrollLock';
import { ModalActionError } from '@/ui/ModalActionError';
import useSessionStore from '@/state/session';
import { useToasts } from '@/ui/ToastProvider';
import { safeErrorMessage } from '@/utils/appErrors';
import '../../pages/ScheduleTimeTracking.css';

/** Classification-only review; closing this dialog never stops the running clock. */
export default function InstallationTimeAllocation({ entry, onClose, started = false }: {
  entry: TimeEntry; onClose: () => void; started?: boolean;
}) {
  const id = React.useId();
  const profileId = useSessionStore(state => state.profileId);
  const epoch = useSessionStore(state => state.principalEpoch);
  const preview = useSessionStore(state => state.isAccessPreview);
  const client = useQueryClient();
  const ref = React.useRef<HTMLDialogElement>(null);
  const heading = React.useRef<HTMLHeadingElement>(null);
  const [purpose, setPurpose] = React.useState(entry.installation_labor_purpose ?? (entry.needs_allocation ? '' : entry.allocation_label ?? ''));
  const { push } = useToasts();
  const [reason, setReason] = React.useState('');
  const [validation, setValidation] = React.useState('');
  useModalScrollLock(true);
  const options = useQuery({ queryKey: ['installation-time-options', profileId, epoch, preview, entry.entry_id],
    queryFn: () => fetchInstallationTimeOptions(entry.entry_id), enabled: !preview,
    // Keep the version the user reviewed; a focus refetch must not silently authorize overwriting a competing save.
    staleTime: Infinity, gcTime: 0, refetchOnWindowFocus: false, refetchOnReconnect: false });
  const current = options.data?.entry ?? entry;
  const mutation = useMutation({
    mutationFn: () => allocateInstallationTime(current, purpose || null, reason.trim()),
    onSuccess: async () => {
      // The write is acknowledged before refreshing. A refresh failure must not invite a second save.
      await Promise.allSettled(['installation', 'job-time-history', 'schedule-time-history', 'schedule-time-launch', 'schedule-time-active', 'labor-calendar', 'record-audit', 'schedule-time-shift-entry']
        .map(key => client.invalidateQueries({ queryKey: [key] })));
      push({ type: 'success', message: `Work type saved: ${purpose || 'Needs review'}.` });
      onClose();
    },
  });
  React.useEffect(() => {
    const node = ref.current;
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    node?.showModal();
    heading.current?.focus({ preventScroll: true });
    return () => { node?.close(); if (trigger?.isConnected) trigger.focus({ preventScroll: true }); };
  }, []);
  const purposes = options.data?.purposes ?? [];
  const choices = purpose && !purposes.includes(purpose) ? [purpose, ...purposes] : purposes;
  const dismiss = () => { if (!mutation.isPending) onClose(); };
  return createPortal(
    <dialog ref={ref} className="modal schedule-time-modal installation-time-allocation" data-editing="true" aria-labelledby={id}
      onCancel={event => { event.preventDefault(); event.stopPropagation(); dismiss(); }}
      onKeyDown={event => { if (event.key === 'Escape' || event.key === 'Tab') event.stopPropagation(); }}>
      <form className="schedule-time__form" noValidate aria-busy={mutation.isPending} onSubmit={event => {
        event.preventDefault();
        if (mutation.isPending || !options.data) return;
        if (current.clock_out && !reason.trim()) { setValidation('Enter a reason for changing this work type.'); return; }
        setValidation(''); mutation.mutate();
      }}>
        <header className="modal__header schedule-time__header">
          <div><span className="schedule-time__eyebrow">{entry.job_code} · {entry.person_name}</span>
            <h2 id={id} ref={heading} tabIndex={-1}>{started ? 'What are you working on?' : 'Change work type'}</h2></div>
          <button type="button" className="btn-close" aria-label="Close work type" disabled={mutation.isPending} onClick={dismiss}>&times;</button>
        </header>
        <div className="modal__body-content schedule-time__form-body">
          <p>{started ? 'Your clock is running. Choose its work type, or review it later. Actual hours appear after clock-out.' : 'Choose the work type for this time entry. Clock times, lunch and quoted prices stay the same.'}</p>
          {options.isPending ? <p role="status">Loading visit purposes…</p> : null}
          {options.data ? <div className="schedule-time__fields">
            <label>Work type<select className="input" disabled={mutation.isPending} value={purpose} onChange={event => setPurpose(event.target.value)}>
              <option value="">Unallocated — assign later</option>{choices.map(item => <option key={item} value={item}>{item}</option>)}
            </select></label>
          </div> : null}
          {current.clock_out ? <label className="schedule-time__reason">Reason for work-type change<textarea className="input resize-none" rows={3} maxLength={2000} value={reason}
            disabled={mutation.isPending} onChange={event => setReason(event.target.value)} aria-invalid={Boolean(validation)} /></label> : null}
          {validation ? <p role="alert" className="schedule-time__error">{validation}</p> : null}
          <ModalActionError message={options.isError ? safeErrorMessage(options.error, 'Work types could not be loaded.') : null}
            actionLabel="Retry options" onAction={() => { void options.refetch(); }} />
          <ModalActionError message={mutation.isError ? safeErrorMessage(mutation.error, 'Work type could not be saved.') : null}
            title="Work type was not saved" hint="Your selections are still here. Refresh the options if someone else changed this entry, then try again."
            actionLabel="Refresh options" onAction={() => { void options.refetch(); }} />
        </div>
        <footer className="modal__footer schedule-time__dialog-actions">
          {started ? <button type="button" className="btn btn-outline" disabled={mutation.isPending} onClick={dismiss}>Assign later</button> : null}
          <button type="submit" className="btn btn-primary" disabled={mutation.isPending || !options.data} aria-busy={mutation.isPending}>{mutation.isPending ? 'Saving…' : 'Save work type'}</button>
        </footer>
      </form>
    </dialog>, document.body,
  );
}
